import React, { useEffect, useState, useContext } from 'react';
import auth0 from '../authentication/index';

import { getTripsPaginated, startListenEvents } from '../api/tripService';
import { getUserProfile, verifyIfUserExists, getOrganizations, getAllOrganizations, updateUserRole, updateUserReadOnlyMode, updateUserCatalogEdition, updateUserRestricted, checkUserRegistry } from '../api/users';
import { getOrganizationByExternalId } from '../api/organizations'
import { areAllFounded } from '../utils';
import { DASHBOARD_VIEW_TYPE, SELECTED_ORG, SELECTED_ROLE } from '../constants';
import { noop } from 'lodash';

let userState;

const initialState = {
  token: null,
  user: null,
  userData: null,
  loading: false,
  organizations: [],
  isAuthenticated: false,
  currentOrganization: null,
  events: null,
  dashboardType: null,
};

const SessionContext = React.createContext(initialState);

const SessionProvider = ({ value, children }) => {
  const { user } = value;

  useEffect(() => {
    if (!userState && user) {
      userState = user;
    }
  }, []);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

const useSessionContext = () => {
  const context = useContext(SessionContext);
  return {
    ...context,
    login: () => auth0.loginWithRedirect(),
    logout: () =>
      auth0.logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      }),
  };
};

const useSession = () => {

  const [session, setSession] = useState({
    ...initialState,
    user: userState || null,
    loading: userState === undefined,
  });

  const [userEvents, setUserEvents] = useState(null);
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {

    if (userState !== undefined) {
      return;
    }
    let isMounted = true;

    async function fetchUser() {

      try {
        let token = localStorage.getItem("externalToken")
        const isExternalSession = !!(token && token !== "null");

        if (!isExternalSession) {
          try {
            token = await auth0.getTokenSilently();
          } catch {
          }
        }

        if (!token) {
          throw "Token not found";
        }

        /** El flujo de externalToken (portal embebido) usa un JWT interno
         * (userId/organizationId) validado contra la BD en cada request por
         * el backend — no un usuario de Auth0 con `sub`. check-user-registry
         * sirve para dar de alta en la BD a usuarios nuevos que llegan por
         * Auth0 nativo; no aplica a este flujo. */
        if (!isExternalSession) {
          const userRegistry = await getUserRegistry(token);

          if (!userRegistry)
            auth0.logout();
        }

        let organizations = await getOrganizations(token);



        if (!organizations.length) {

          auth0.logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          })
          return;
        }



        const allOrganizations = await getAllOrganizations(token);
        let [firstOrg] = organizations;
        let storedOrgId = window.localStorage.getItem(SELECTED_ORG);

        let currentOrganization = organizations.find((org) => org.isCurrent);
        if (!currentOrganization)
          currentOrganization = organizations.find((org) => org.id === storedOrgId) || firstOrg;


        const user = await getUserProfile(currentOrganization.id, token);
        const userData = await verifyIfUserExists({ email: user.email });

        /** Si no es un usuario activo, que te desloguee */
        if (!userData?.userInfo?.isActive) {
          auth0.logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          })
        }



        const { total: tripsTotal } = await getTripsPaginated(
          token,
          currentOrganization.id,
          {
            pageNumber: 1,
            totalPerPage: 1,
          }
        );

        let dashboardType = window.localStorage.getItem(DASHBOARD_VIEW_TYPE.KEY);

        if (!Object.values(DASHBOARD_VIEW_TYPE).includes(dashboardType)) {
          dashboardType = null;
        }

        if (isMounted) {

          if (user) {
            setSession({
              user,
              userData,
              token,
              organizations,
              allOrganizations,
              currentOrganization,
              isAuthenticated: true,
              loading: false,
              tripsTotal,
              dashboardType: dashboardType || DASHBOARD_VIEW_TYPE.KANBAN,
            });

          }
        }

      } catch (e) {
        setSession({ ...initialState, loading: false });

        if (e)
          console.log(e);

      }
    }

    fetchUser();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;
    let hubConnection = null;

    const connectTripEventsHub = async (organizationId) => {
      hubConnection = await startListenEvents(organizationId);

      const onTripUpdated = (tripUpdated, tripEvent) => {
        if (!isMounted) return;

        setUserEvents({ tripUpdated, tripEvent });

        if (tripUpdated.status === "sendingShipments") {
          hubConnection.stop();
          timeoutId = setTimeout(() => {
            if (isMounted) {
              hubConnection.start();
            }
          }, 5000);
        }
      };
      if (hubConnection) {
        hubConnection.on("tripUpdated", onTripUpdated);
      }
    };
    if (session?.currentOrganization?.id)
      connectTripEventsHub(session.currentOrganization.id);

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (hubConnection) {
        hubConnection.off("tripUpdated"); // elimina listener
        hubConnection.stop();
      }
    };
  }, [session?.currentOrganization?.id]);


  const resetUser = async () => {
    async function fetchUser() {
      try {
        let token = localStorage.getItem("externalToken")

        if (!token || token == "null") {
          try {
            token = await auth0.getTokenSilently();
          } catch {
          }
        }

        if (!token) {
          throw "Token not found";
        }

        const user = await getUserProfile(session.currentOrganization.id, token);

        if (isMounted) {
          if (user) {
            setSession({
              ...session,
              user
            });
          }
        }
      } catch (e) {
        setSession({ ...initialState, loading: false });

        if (e)
          console.log(e);

      }


    }
    fetchUser();
  };

  const refreshUserAsync = async (orgId, from = noop) => {
    setSession({
      ...session,
      loading: true,
    });
    window.localStorage.setItem(SELECTED_ORG, orgId);

    const token = await auth0.getTokenSilently();
    const user = await getUserProfile(orgId, token);
    let allOrganizationsList = session && session.organizations

    let currentOrganization = allOrganizationsList.find((org) => org.id === orgId);

    setSession({
      ...session,
      currentOrganization,
      user,
      loading: false,
    });
  };

  const refreshCurrentOrganizationAsync = async (values) => {

    let currentOrganization = { ...currentOrganization, ...values }
    setSession({
      ...session,
      currentOrganization,
    });
  };

  const refreshUserRoleAsync = async (role, userId, orgId) => {
    //Buscar usuario 
    const token = await auth0.getTokenSilently();
    const resultUpdateUserRole = await updateUserRole(token, userId, role, orgId);
    //Actualizar rol
    if (resultUpdateUserRole) {
      return resultUpdateUserRole && resultUpdateUserRole.newData
    }
  };

  const refreshUserReadOnlyMode = async (readOnly, userId, orgId) => {
    //Buscar usuario 
    const token = await auth0.getTokenSilently();
    const resultUpdateUserReadOnly = await updateUserReadOnlyMode(token, userId, readOnly, orgId);
    //Actualizar rol
    if (resultUpdateUserReadOnly) {
      return resultUpdateUserReadOnly && resultUpdateUserReadOnly.newData
    }
  };

  const refreshUserCatalogEdition = async (catalogEdition, userId, orgId) => {
    //Buscar usuario 
    const token = await auth0.getTokenSilently();
    const resultUpdateUserCatalogEdition = await updateUserCatalogEdition(token, userId, catalogEdition, orgId);
    //Actualizar rol
    if (resultUpdateUserCatalogEdition) {
      return resultUpdateUserCatalogEdition && resultUpdateUserCatalogEdition.newData
    }
  };

  const refreshUserRestricted = async (userRestricted, userId, orgId) => {
    //Buscar usuario 
    const token = await auth0.getTokenSilently();
    const resultUpdateUserRestricted = await updateUserRestricted(token, userId, userRestricted, orgId);
    //Actualizar rol
    if (resultUpdateUserRestricted) {
      return resultUpdateUserRestricted && resultUpdateUserRestricted.newData
    }
  };
  const refreshCurrentOrganization = ({ id, name, tenantId }) => {
    const organizations = session.organizations.filter((org) => org.id !== id);

    let currentOrganization = session.organizations.find((org) => org.id == id);
    currentOrganization = { ...currentOrganization, name: name, tenantId: tenantId }
    setSession({
      ...session,
      organizations: [...organizations, currentOrganization],
      currentOrganization,
    });
  };

  const refreshOrganizations = async () => {
    const token = await auth0.getTokenSilently();
    const organizations = await getOrganizations(token);
    setSession({
      ...session,
      organizations
    });
  };

  const setDashboardType = (dashboardType) => {
    window.localStorage.setItem(DASHBOARD_VIEW_TYPE.KEY, dashboardType);
    setSession({
      ...session,
      dashboardType,
    });
  };

  const getUserRegistry = async (token) => {
    try {

      const user = await auth0.getUser();
      const userRegistry = await checkUserRegistry(token, user);
      if (userRegistry)
        return userRegistry[0]
      else return null;
    } catch (ex) {
      console.error("Error checking user registry:", ex);
      await auth0.logout();
      return null;
    }
  };

  return {
    ...session,
    events: { ...userEvents },
    refreshUserAsync,
    refreshCurrentOrganizationAsync,
    refreshUserRoleAsync,
    setDashboardType,
    refreshCurrentOrganization,
    refreshOrganizations,
    refreshUserReadOnlyMode,
    refreshUserCatalogEdition,
    refreshUserRestricted,
    resetUser
  };
};

export { useSessionContext, SessionProvider, useSession };
